import { Button, Col, Empty, Row } from 'antd';
import { get } from 'lodash';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getSim900WaitingActivationById } from 'src/api/activeSim';
import BoxWithLabel from 'src/components/BoxWithLabel/BoxWithLabel';
import Sim900StatusTag, {
  getSim900StatusTagConfig,
} from 'src/pages/management/activeSim/pages/sim900/components/Sim900StatusTag';
import { useAppSelector } from 'src/redux/hook';
import { setGlobalState } from 'src/redux/slice/globalSlice';
import { Sim900WaitingActivationStatus } from 'src/shared/enum/sim.enum';
import { Sim900WaitingActivationDetail } from 'src/shared/interface/sim900.interface';
import { formatDateTime, formatPhoneNumberV2, parseQueryString } from 'src/utils';
import { simTypeToText } from 'src/utils/sim.enum';

const getNullableText = (value?: string | number | null) => {
  return value === undefined || value === null || value === '' ? '-' : value.toString();
};

const getDateTimeText = (value?: string | null) => {
  return value ? formatDateTime(value) : '-';
};

const getPhoneNumberText = (phoneNumber?: string | null) => {
  if (!phoneNumber) {
    return '-';
  }

  return formatPhoneNumberV2(phoneNumber);
};

const getSimTypeText = (simType?: string | null) => {
  if (!simType) {
    return '-';
  }

  const simTypeNumber = Number(simType);

  return Number.isNaN(simTypeNumber) ? simType : simTypeToText(simTypeNumber);
};

type DetailRowProps = {
  label: string;
  children: ReactNode;
  classNames?: { label?: string; value?: string };
};

function DetailField({ label, children, classNames }: DetailRowProps) {
  return (
    <div className={`mb-5 flex items-center justify-between`}>
      <div className={`mb-1 text-sm font-semibold text-[#4b5563] ${classNames?.label}`}>
        {label}
      </div>
      <div className={`break-words text-sm font-medium text-[#111827] ${classNames?.value}`}>
        {children}
      </div>
    </div>
  );
}

const getCreatedByText = (detail: Sim900WaitingActivationDetail) => {
  return getNullableText(detail.creatorName || detail.createdBy || detail.merchantId);
};

const getIssueEmployeeText = (detail: Sim900WaitingActivationDetail) => {
  return getNullableText(detail.shopCode);
};

export default function Sim900WaitingActivationDetailPage() {
  const location = useLocation();
  const { openTabs, masterData } = useAppSelector((state) => state.global);
  const navigate = useNavigate();
  const [detail, setDetail] = useState<Sim900WaitingActivationDetail>();
  const dispatch = useDispatch();

  const renderSubType = (subType?: string) => {
    if (subType) {
      const objectSubType = masterData?.subType?.find((item: any) => {
        return item?.description === subType;
      });
      if (objectSubType) {
        return get(objectSubType, 'name', subType);
      } else {
        return subType;
      }
    } else {
      return subType;
    }
  };
  const getSubscriberTypeText = (detail: Sim900WaitingActivationDetail) => {
    return renderSubType(getNullableText(detail.subType));
  };
  useEffect(() => {
    const { id } = parseQueryString(location.search);

    if (!id || Array.isArray(id)) {
      setDetail(undefined);
      return;
    }

    getSim900WaitingActivationById(id).then((res) => {
      setDetail(res);
    });
  }, [location.search]);

  if (!detail) {
    return <Empty description='Không có dữ liệu' />;
  }

  const handleRetry = () => {
    dispatch(
      setGlobalState({
        activeKeyMenu: `connection/history/list?id=${detail.serial}&orderId=${detail.orderId}`,
      }),
    );
    if (
      !openTabs.find(
        (tab) =>
          tab.key === `connection/history/list?id=${detail.serial}&orderId=${detail.orderId}`,
      )
    ) {
      dispatch(
        setGlobalState({
          openTabs: [
            ...openTabs,
            {
              label: 'Xem lịch sử đấu nối ' + detail.serial,
              children: '',
              key: `connection/history/list?id=${detail.serial}&orderId=${detail.orderId}`,
              closable: true,
            },
          ],
        }),
      );
    }
    navigate(`/management/connection/history/list?id=${detail?.serial}&orderId=${detail?.orderId}`);
  };

  const renderDateByStatus = (status: Sim900WaitingActivationStatus | undefined) => {
    if (!status) return '-';
    const { importantTextClassName } = getSim900StatusTagConfig(status);
    switch (status) {
      case Sim900WaitingActivationStatus.Created:
      case Sim900WaitingActivationStatus.WaitingActivation900: {
        const errorTextClassName = getSim900StatusTagConfig(
          Sim900WaitingActivationStatus.Error,
        ).importantTextClassName;
        return (
          <DetailField
            classNames={{ value: `${errorTextClassName} !font-bold` }}
            label='Ngày hết hạn kích hoạt'
          >
            {formatDateTime(detail.activationExpiredAt ?? '')}
          </DetailField>
        );
      }
      case Sim900WaitingActivationStatus.Activated:
      case Sim900WaitingActivationStatus.Completed: {
        return (
          <DetailField
            classNames={{ value: `${importantTextClassName} !font-bold` }}
            label='Ngày kích hoạt'
          >
            {formatDateTime(detail.activatedAt ?? '')}
          </DetailField>
        );
      }
      case Sim900WaitingActivationStatus.Canceled: {
        return (
          <DetailField
            classNames={{ value: `${importantTextClassName} !font-bold` }}
            label='Ngày hủy'
          >
            {formatDateTime(detail.canceledAt ?? '')}
          </DetailField>
        );
      }
      default: {
        return null;
      }
    }
  };
  return (
    <>
      <div className='mb-6 flex justify-end gap-4'>
        {detail.status === Sim900WaitingActivationStatus.Created && (
          <Button
            type='primary'
            disabled={detail.status !== Sim900WaitingActivationStatus.Created}
            onClick={handleRetry}
            className='w-full sm:w-auto'
          >
            Retry
          </Button>
        )}
      </div>
      <Row gutter={[16, 0]}>
        <Col span={12}>
          <BoxWithLabel title='Thông tin kích hoạt' classNames='mb-6'>
            <DetailField label='Trạng thái'>
              <Sim900StatusTag status={detail.status} />
            </DetailField>

            <DetailField label='Thời gian tạo'>{getDateTimeText(detail.createdAt)}</DetailField>
            {renderDateByStatus(detail.status)}
            <DetailField label='Người tạo'>{getCreatedByText(detail)}</DetailField>
            <DetailField label='Doanh nghiệp tạo'>
              {getNullableText(detail.createdEnterprise || detail.enterpriseName)}
            </DetailField>
            <DetailField label='Thời gian cập nhật'>
              {getDateTimeText(detail.updatedAt)}
            </DetailField>
          </BoxWithLabel>
        </Col>
        <Col span={12}>
          <BoxWithLabel title='Thông tin SIM' classNames='mb-6'>
            <DetailField label='Serial'>{getNullableText(detail.serial)}</DetailField>
            <DetailField label='Số thuê bao'>{getPhoneNumberText(detail.phoneNumber)}</DetailField>

            <DetailField label='Loại sim'>{getSimTypeText(detail.simType)}</DetailField>
            <DetailField label='Mã gói cước'>{getNullableText(detail.package)}</DetailField>

            <DetailField label='Nhà phân phối'>{getIssueEmployeeText(detail)}</DetailField>
            <DetailField label='Loại thuê bao'>{getSubscriberTypeText(detail)}</DetailField>
          </BoxWithLabel>
        </Col>
      </Row>
      <p className='m-0 text-center text-xs text-[#9ca3af]'>
        Hệ thống quản lý thuê bao ASIM © 2024. Tất cả dữ liệu được bảo mật.{' '}
      </p>
    </>
  );
}
